import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import PageTitle from '../../components/PageTitle';
import { NoticeScreenProps } from './types';
import { Button } from '../../components/Button';
import { STYLE_VARS } from '../../constants/style-vars';
import { Image } from '../../components/Image';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../services/redux/store';
import NoticeItem from './components/NoticeItem';
import { Footer } from '../../components/Footer';
import { COLORS, FONTS } from '../../constants';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import NoticeLayout from '../../WebLayout/NoticeLayout/NoticeLayout';
import { NoticesApi } from '../../services/api';
import { Loading } from '../../components';
import {
  NoticeFilter,
  clearNoticeFilter,
  setLoadingNotice,
  setNoticeFilter,
  setNotices,
  setTotal,
} from '../../services/redux/reducers/NoticeSlice';
import { debounce } from 'lodash';
import { TextInput } from '../../components/TextInput/TextInput';
import Filter from '../../components/Filter/Filter';
import { INoticeCategory } from '../../types/notices.types';
import { Category } from '../../types/categories.types';

const Notices: FC<NoticeScreenProps> = ({ navigation, route }) => {
  const screen = route.params.screen;
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();

  const [toggleFilter, setToggleFilter] = useState<boolean>(false);
  const [filterSelected, setFilterSelected] = useState<Category[]>([]);

  const { noticeFilter, noticesData, total, isLoading } = useSelector(
    (state: RootState) => state.noticeReducer
  );
  const [type, setType] = useState<'new' | 'past' | 'all'>('new');

  const getNotices = useCallback(
    async (props: NoticeFilter) => {
      try {
        dispatch(setLoadingNotice(true));
        const res = await NoticesApi.getNotices(props);
        dispatch(setNotices(res.data.notices));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingNotice(false));
      }
    },
    [dispatch]
  );

  const handleLoadmore = useCallback(async () => {
    if (noticesData.length < total) {
      dispatch(
        setNoticeFilter({
          ...noticeFilter,
          page: Math.ceil(noticesData.length / 5) + 1,
        })
      );
      try {
        dispatch(setLoadingNotice(true));
        const res = await NoticesApi.getNotices({
          ...noticeFilter,
          page: Math.ceil(noticesData.length / 5) + 1,
        });
        dispatch(setNotices([...noticesData, ...res.data.notices]));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingNotice(false));
      }
    }
  }, [noticesData, total, dispatch, noticeFilter]);

  useEffect(() => {
    dispatch(clearNoticeFilter());
    getNotices({ type: screen });
  }, [dispatch, getNotices, screen]);

  const newNoticeButton = useMemo(() => {
    return (
      <Button
        text="NEW NOTICES"
        className={type === 'new' ? 'green-button' : ''}
        style={
          type === 'new'
            ? STYLE_VARS.fullWGreenButton
            : STYLE_VARS.unActiveButton
        }
        arrowStyle={
          type !== 'new' && {
            tintColor: STYLE_VARS.primaryColor,
          }
        }
        textStyle={{
          color: type === 'new' ? 'white' : STYLE_VARS.textColor,
        }}
        onPress={() => {
          dispatch(setNotices([]));
          setType('new');
          getNotices({
            ...noticeFilter,
            type: 'new',
            page: 1,
          });
          dispatch(
            setNoticeFilter({
              ...noticeFilter,
              type: 'new',
              page: 1,
            })
          );
        }}
      />
    );
  }, [type, dispatch, noticeFilter, getNotices]);

  const pastNoticeButton = useMemo(() => {
    return (
      <Button
        text="PAST NOTICES"
        className={type === 'past' ? 'green-button' : ''}
        arrowStyle={
          type !== 'past' && {
            tintColor: STYLE_VARS.primaryColor,
          }
        }
        style={
          type === 'past'
            ? STYLE_VARS.fullWGreenButton
            : STYLE_VARS.unActiveButton
        }
        textStyle={{
          color: type === 'past' ? 'white' : STYLE_VARS.textColor,
        }}
        onPress={() => {
          setType('past');
          dispatch(setNotices([]));
          getNotices({
            ...noticeFilter,
            type: 'past',
            page: 1,
          });
          dispatch(
            setNoticeFilter({
              ...noticeFilter,
              type: 'past',
              page: 1,
            })
          );
        }}
      />
    );
  }, [type, dispatch, noticeFilter, getNotices]);

  const handleSearch = useCallback(
    (text: string) => {
      dispatch(setNotices([]));
      getNotices({
        ...noticeFilter,
        page: 1,
        search: text,
        type: type,
      });
      dispatch(
        setNoticeFilter({
          ...noticeFilter,
          page: 1,
          search: text,
          type: type,
        })
      );
    },
    [noticeFilter, dispatch, getNotices, type]
  );

  const debouncedSearch = debounce(handleSearch, 500);
  const [noticeCategories, setNoticeCategories] = useState<INoticeCategory[]>(
    []
  );

  const getNoticeCategoriesData = useCallback(async () => {
    try {
      const res = await NoticesApi.getNoticeCategories({});
      setNoticeCategories(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, []);

  useEffect(() => {
    getNoticeCategoriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isDesktop()) {
    return (
      <NoticeLayout>
        <View
          style={{
            flexDirection: 'row',
            borderBottomColor: STYLE_VARS.fccDarkGrey,
            borderBottomWidth: 1,
            paddingBottom: 20,
          }}
        >
          <View
            style={{
              alignItems: 'center',
              flexDirection: 'row',
              display: 'flex',
              gap: 10,
              width: '40%',
              minWidth: 350,
            }}
          >
            <View style={{ flex: 1 }}>{newNoticeButton}</View>
            <View style={{ flex: 1 }}>{pastNoticeButton}</View>
          </View>
        </View>
        <View>
          <View
            style={{
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {noticesData &&
              noticesData.map((notice) => (
                <NoticeItem item={notice} key={notice.id} />
              ))}
          </View>
          {isLoading === false && noticesData.length === 0 ? (
            <View style={styles.noDataWrapper}>
              <Text style={[styles.noDataText, { fontSize: 20 }]}>
                THERE ARE CURRENTLY NO NOTICES
              </Text>
            </View>
          ) : (
            <></>
          )}
          {isLoading && <Loading style={{ paddingVertical: 30 }} />}
          {total > noticesData.length && (
            <View
              style={{
                marginTop: 30,
                marginBottom: 50,
                alignSelf: 'center',
              }}
            >
              <Button
                text="VIEW MORE"
                style={STYLE_VARS.blueButton}
                onPress={handleLoadmore}
                disabled={isLoading}
              />
            </View>
          )}
        </View>
      </NoticeLayout>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.container}>
        <PageTitle
          title="Notices"
          icon={require('../../assets/notices.png')}
          haveGoBackButton={true}
        />
      </View>
      <View style={{ flexDirection: 'row', paddingHorizontal: 20, gap: 20 }}>
        {newNoticeButton}
        {pastNoticeButton}
      </View>
      <View style={[styles.container, { zIndex: 5 }]}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginVertical: 20,
          }}
        >
          <TouchableOpacity
            style={styles.navbarWrapper}
            onPress={() => setToggleFilter(!toggleFilter)}
          >
            <Text style={styles.textGreen}>Filter</Text>
            <Image
              source={require('../../assets/filter-button.png')}
              style={styles.navbarIconStyle}
            />
          </TouchableOpacity>
          <View style={styles.navbarWrapper}>
            <View style={{ width: 80 }}>
              <TextInput
                placeholder="SEARCH"
                placeholderTextColor={STYLE_VARS.primaryColor}
                onChangeText={debouncedSearch}
                style={{
                  fontSize: 16,
                  fontWeight: '500',
                  color: STYLE_VARS.primaryColor,
                  fontFamily: FONTS.IowanOldStyleBTRoman,
                  paddingHorizontal: 6,
                  paddingVertical: 1,
                  flex: 1,
                }}
              />
            </View>
            <Image
              source={require('../../assets/search_white.png')}
              style={styles.navbarIconStyle}
            />
          </View>
        </View>
        {toggleFilter && (
          <Filter
            categoriesData={noticeCategories}
            getData={getNotices}
            filterData={noticeFilter}
            setFilterData={setNoticeFilter}
            setData={setNotices}
            filterSelected={filterSelected}
            setFilterSelected={setFilterSelected}
          />
        )}
      </View>
      <View style={styles.container}>
        {noticesData.map((notice) => (
          <NoticeItem item={notice} key={notice.id} />
        ))}
      </View>
      {isLoading === false && noticesData.length === 0 ? (
        <View style={styles.noDataWrapper}>
          <Text
            style={[styles.noDataText, { fontSize: 16, paddingBottom: 250 }]}
          >
            THERE ARE CURRENTLY NO NOTICES
          </Text>
        </View>
      ) : (
        <></>
      )}
      {isLoading && <Loading style={{ paddingVertical: 30 }} />}
      {total > noticesData.length && (
        <View style={{ paddingVertical: 40 }}>
          <Button
            style={STYLE_VARS.blueButton}
            text="VIEW MORE"
            onPress={handleLoadmore}
            disabled={isLoading}
          />
        </View>
      )}
      <Footer />
    </ScrollView>
  );
};

export default Notices;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  navbarWrapper: {
    width: '40%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  navbarIconStyle: {
    width: 15,
    height: 15,
    resizeMode: 'contain',
    tintColor: STYLE_VARS.primaryColor,
    marginLeft: 5,
  },
  textGreen: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 16,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  noDataWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  noDataText: {
    color: COLORS.GREEN,
    textTransform: 'uppercase',
    fontFamily: FONTS.AvenirMedium,
  },
});
